/**
 * @mixin trackingMixin
 * @description Mixin para centralizar tracking de eventos con GTM y Amplitude.
 * Evita duplicación de lógica de analytics en componentes.
 * 
 * @example
 * // En un componente Vue:
 * import trackingMixin from '@/mixins/trackingMixin'
 * 
 * export default {
 *   name: 'MyComponent',
 *   mixins: [trackingMixin],
 *   methods: {
 *     handleSuccess() {
 *       this.trackEvent('GTM', {
 *         event: 'virtualEvent',
 *         category: 'Zona Segura',
 *         action: 'Action Name',
 *         label: 'Label'
 *       })
 *     }
 *   }
 * }
 */

export default {
  methods: {
    /**
     * Registra un evento de tracking en GTM y/o Amplitude.
     * @param {string} provider - Proveedor de analytics: 'GTM', 'AMPLITUDE' o 'ALL'
     * @param {Object} eventData - Datos del evento a trackear
     * @param {Object} options - Opciones adicionales
     */
    trackEvent(provider, eventData, options = {}) {
      try {
        if (!eventData) {
          console.warn('[Tracking] No event data provided')
          return
        }

        const providers = provider === 'ALL' ? ['GTM', 'AMPLITUDE'] : [provider]

        providers.forEach(p => {
          switch (p) {
            case 'GTM':
              this._trackGTMEvent(eventData)
              break
            case 'AMPLITUDE':
              this._trackAmplitudeEvent(eventData, options)
              break
            default:
              console.warn(`[Tracking] Unknown provider: ${p}`)
          }
        })
      } catch (error) {
        // No lanzar error para no interrumpir flujo del usuario
        console.error('[Tracking] Error tracking event')
      }
    },

    /**
     * Registra evento GTM cuando se muestra un popup de validación/error.
     * los flujos de préstamos (Operations, Home, LoansFirstStep, LoansThridStep).
     * @param {string} cta - Texto descriptivo del mensaje mostrado en el popup
     */
    trackValidationPopup(cta) {
      this.trackEvent('GTM', {
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Popup validacion',
        TipoAccion: 'Mostrar',
        CTA: cta
      })
    },

    /**
     * Registra un pageview en GTM.
     * @param {string} pagePath - Ruta de la página
     * @param {string} pageTitle - Título de la página
     */
    trackPageview(pagePath, pageTitle) {
      try {
        if (!this.$gtm) {
          return
        }

        this.$gtm.trackEvent({
          event: 'pageview',
          pagePath: pagePath || window.location.pathname,
          pageTitle: pageTitle || document.title
        })
      } catch (error) {
        console.error('[Tracking] Error tracking pageview')
      }
    },

    /**
     * Registra evento de éxito de operación SOAT.
     * @param {Object} data - Datos del evento
     * @param {string} data.action - Acción realizada
     * @param {string} data.policy - Número de póliza (será ofuscado)
     * @param {string} data.requestCode - Código de solicitud
     */
    trackSOATSuccess(data) {
      const { action, policy, requestCode } = data

      // GTM
      this.trackEvent('GTM', {
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: action || 'SOAT Operation Success',
        label: requestCode ? `Solicitud N° ${requestCode}` : 'Solicitud enviada exitosamente'
      })

      // Amplitude (con datos ofuscados)
      this.trackEvent('AMPLITUDE', {
        eventName: 'SOAT_OPERATION_SUCCESS',
        eventProperties: {
          action: action || 'unknown',
          policyMasked: this._maskSensitiveData(policy),
          requestCode: requestCode || 'N/A'
        }
      })
    },

    /**
     * Registra evento de error de operación SOAT.
     * @param {Object} data - Datos del evento
     * @param {string} data.action - Acción que falló
     * @param {string} data.policy - Número de póliza (será ofuscado)
     * @param {string} data.errorType - Tipo de error (NO incluir mensaje de error)
     */
    trackSOATError(data) {
      const { action, policy, errorType } = data

      // Solo trackear tipo de error, NO el mensaje
      this.trackEvent('AMPLITUDE', {
        eventName: 'SOAT_OPERATION_ERROR',
        eventProperties: {
          action: action || 'unknown',
          policyMasked: this._maskSensitiveData(policy),
          errorType: errorType || 'unknown_error'
        }
      })
    },

    /**
     * Trackea evento en Google Tag Manager.
     * @private
     * @param {Object} eventData - Datos del evento GTM
     */
    _trackGTMEvent(eventData) {
      if (!this.$gtm) {
        return
      }

      this.$gtm.trackEvent(eventData)
    },

    /**
     * Trackea evento en Amplitude.
     * @private
     * @param {Object} eventData - Datos del evento
     * @param {Object} options - Opciones adicionales
     */
    _trackAmplitudeEvent(eventData, options = {}) {
      if (!this.$amplitude) {
        return
      }

      const { eventName, eventProperties } = eventData

      if (!eventName) {
        console.warn('[Tracking] Amplitude event requires eventName')
        return
      }

      this.$amplitude.getInstance().logEvent(eventName, eventProperties || {})
    },

    /**
     * Ofusca datos sensibles para tracking.
     * Solo muestra los últimos 4 caracteres.
     * @private
     * @param {string} data - Dato a ofuscar
     * @returns {string} - Dato ofuscado
     */
    _maskSensitiveData(data) {
      if (!data || typeof data !== 'string') {
        return 'N/A'
      }

      if (data.length <= 4) {
        return '****'
      }

      return '****' + data.slice(-4)
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/trackingMixin.js